import type {
  CustomerAddressCreate,
  CustomerSupportCategory,
  NotificationFeedItem,
  NotificationPreferences,
} from "@gobro/contracts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, CustomerApi } from "./api";
import type {
  BusinessType,
  Cart,
  CatalogItem,
  CustomerAddress,
  CustomerCatalog,
  CustomerOrder,
  Locale,
  ModifierGroup,
  Quote,
  StoreSummary,
  Tab,
  TrackingSnapshot,
} from "./domain";
import {
  readFavorites,
  recordPendingFavoriteOp,
  syncFavorites,
  toggleFavoriteLocal,
} from "./favorites";
import { distance, money, shortDate } from "./format";
import { tr } from "./i18n";
import { Brand, Icon, type IconName } from "./icons";
import { LiveTrackingMap } from "./live-map";
import { type CustomerCache, IndexedDbCustomerCache, MemoryCustomerCache } from "./offline-store";
import { connectNotificationStream, subscribeWebPush } from "./push";
import "./styles.css";

type Screen =
  | "auth"
  | "checkout"
  | "home"
  | "notifications"
  | "orders"
  | "profile"
  | "store"
  | "tracking";

interface PreviewData {
  address: CustomerAddress;
  catalog: CustomerCatalog;
  stores: StoreSummary[];
}

interface AppProps {
  api?: CustomerApi;
  cache?: CustomerCache;
  previewData?: PreviewData;
}

const DEFAULT_LOCATION = { latitude: 36.8065, longitude: 10.1815 };

export function App({ api: apiInput, cache, previewData }: AppProps) {
  const api = useMemo(() => apiInput ?? new CustomerApi(), [apiInput]);
  const storage = useMemo(
    () => cache ?? (previewData ? new MemoryCustomerCache() : new IndexedDbCustomerCache()),
    [cache, previewData],
  );
  const [locale, setLocale] = useState<Locale>("ar");
  const [screen, setScreen] = useState<Screen>(previewData ? "home" : "home");
  const [tab, setTab] = useState<Tab>("home");
  const [online, setOnline] = useState(navigator.onLine);
  const [cached, setCached] = useState(false);
  const [busy, setBusy] = useState(!previewData);
  const [message, setMessage] = useState<string | null>(null);
  const [authenticated, setAuthenticated] = useState(Boolean(previewData));
  const [stores, setStores] = useState<StoreSummary[]>(previewData?.stores ?? []);
  const [catalog, setCatalog] = useState<CustomerCatalog | null>(previewData?.catalog ?? null);
  const [addresses, setAddresses] = useState<CustomerAddress[]>(
    previewData ? [previewData.address] : [],
  );
  const [address, setAddress] = useState<CustomerAddress | null>(previewData?.address ?? null);
  const [cart, setCart] = useState<Cart | null>(() => readCart());
  const [favorites, setFavorites] = useState<Set<string>>(() => readFavorites());
  const [quote, setQuote] = useState<Quote | null>(null);
  const [orders, setOrders] = useState<CustomerOrder[]>([]);
  const [activeOrder, setActiveOrder] = useState<CustomerOrder | null>(null);
  const [tracking, setTracking] = useState<TrackingSnapshot | null>(null);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const trackingCursor = useRef(0);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<BusinessType | "">("");
  const [afterAuth, setAfterAuth] = useState<Screen>("home");
  const booted = useRef(false);
  const guestLocation = useMemo(() => readGuestLocation(), []);
  const currentLocation = address?.location ?? guestLocation;
  const activeOrderId = activeOrder?.id;

  const loadStores = useCallback(
    async (targetLocale = locale, targetCategory = category, query = search) => {
      setBusy(true);
      try {
        const page = await api.stores(targetLocale, currentLocation, query, targetCategory);
        setStores(page.items);
        setCached(false);
        await storage.putStores(targetLocale, page.items);
      } catch (error) {
        const saved = await storage.getStores(targetLocale);
        if (saved) {
          setStores(saved.value);
          setCached(true);
        } else setMessage(errorText(error, targetLocale));
      } finally {
        setBusy(false);
      }
    },
    [api, category, currentLocation, locale, search, storage],
  );

  const loadAddresses = useCallback(async () => {
    const response = await api.addresses();
    setAddresses(response.items);
    const selected = response.items.find((entry) => entry.isDefault) ?? response.items[0] ?? null;
    setAddress(selected);
    return selected;
  }, [api]);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = locale === "ar" ? "rtl" : "ltr";
  }, [locale]);

  useEffect(() => {
    if (previewData || booted.current) return;
    booted.current = true;
    const boot = async () => {
      let initialLocale = locale;
      try {
        const me = await api.me();
        initialLocale = me.locale;
        setLocale(me.locale);
        setAuthenticated(true);
        await loadAddresses();
      } catch (error) {
        if (!(error instanceof ApiError && error.status === 401))
          setMessage(errorText(error, locale));
      }
      await loadStores(initialLocale, "", "");
    };
    void boot();
  }, [api, loadAddresses, loadStores, locale, previewData]);

  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      void loadStores();
    };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, [loadStores]);

  useEffect(() => {
    if (!authenticated || previewData) return;
    const refreshUnread = () =>
      api
        .notificationFeed(null, 1)
        .then((page) => setUnreadNotifications(page.unreadCount))
        .catch(() => undefined);
    void refreshUnread();
    const interval = window.setInterval(() => void refreshUnread(), 30_000);
    const disconnect = connectNotificationStream(api, () => {
      setUnreadNotifications((count) => count + 1);
    });
    return () => {
      window.clearInterval(interval);
      disconnect();
    };
  }, [api, authenticated, previewData]);

  useEffect(() => {
    if (!authenticated || !online || previewData) return;
    let cancelled = false;
    syncFavorites(api)
      .then((merged) => {
        if (!cancelled) setFavorites(merged);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [api, authenticated, online, previewData]);

  useEffect(() => {
    if (!activeOrderId || screen !== "tracking" || previewData) return;
    const abort = new AbortController();
    const refresh = async () => {
      try {
        const [order, live] = await Promise.all([
          api.order(activeOrderId),
          api.trackingSnapshot(activeOrderId),
        ]);
        setActiveOrder(order);
        if (live.available) {
          trackingCursor.current = live.snapshot.cursor;
          setTracking(live.snapshot);
        }
      } catch (error) {
        if (online) setMessage(errorText(error, locale));
      }
    };
    const connect = async () => {
      await refresh();
      try {
        const ticket = await api.trackingTicket(activeOrderId);
        await api.streamTracking(
          ticket.ticket,
          trackingCursor.current,
          abort.signal,
          (snapshot) => {
            trackingCursor.current = snapshot.cursor;
            setTracking(snapshot);
          },
        );
      } catch (error) {
        if (
          !abort.signal.aborted &&
          online &&
          !(error instanceof ApiError && error.status === 404)
        ) {
          setMessage(errorText(error, locale));
        }
      }
    };
    void connect();
    const interval = window.setInterval(() => void refresh(), 8_000);
    return () => {
      abort.abort();
      window.clearInterval(interval);
    };
  }, [activeOrderId, api, locale, online, previewData, screen]);

  const navigateTab = async (next: Tab) => {
    setTab(next);
    setMessage(null);
    if (next === "home" || next === "search" || next === "favorites") {
      setScreen("home");
      if (next === "search")
        window.setTimeout(() => document.getElementById("discovery-search")?.focus(), 0);
      return;
    }
    if (!authenticated) {
      setAfterAuth(next === "orders" ? "orders" : "profile");
      setScreen("auth");
      return;
    }
    if (next === "orders") {
      setScreen("orders");
      await refreshOrders();
    } else setScreen("profile");
  };

  const refreshOrders = async () => {
    if (!online) return;
    try {
      setBusy(true);
      setOrders((await api.orders()).items);
    } catch (error) {
      setMessage(errorText(error, locale));
    } finally {
      setBusy(false);
    }
  };

  const openStore = async (store: StoreSummary) => {
    setBusy(true);
    setMessage(null);
    try {
      const [content, zone] = await Promise.all([
        api.catalog(store.slug, locale),
        api.serviceability(store.slug, currentLocation),
      ]);
      if (!zone.serviceable)
        throw new Error(
          locale === "ar" ? "العنوان خارج نطاق توصيل هذا المتجر" : "Adresse hors zone de livraison",
        );
      setCatalog(content);
      setCached(false);
      await storage.putCatalog(store.slug, locale, content);
      setScreen("store");
    } catch (error) {
      const saved = await storage.getCatalog(store.slug, locale);
      if (saved) {
        setCatalog(saved.value);
        setCached(true);
        setScreen("store");
      } else setMessage(errorText(error, locale));
    } finally {
      setBusy(false);
    }
  };

  const addLine = (item: CatalogItem, selected: string[], quantity: number) => {
    if (!catalog) return;
    const modifierTotal = item.modifierGroups
      .flatMap((group) => group.options)
      .filter((option) => selected.includes(option.id))
      .reduce((sum, option) => sum + option.priceDeltaMillimes, 0);
    const key = `${item.id}:${[...selected].sort().join(",")}`;
    const sameStore = cart?.storeId === stores.find((entry) => entry.slug === catalog.slug)?.id;
    const storeId = stores.find((entry) => entry.slug === catalog.slug)?.id ?? cart?.storeId;
    if (!storeId) return;
    const base: Cart =
      sameStore && cart
        ? cart
        : { lines: [], storeId, storeName: catalog.name, storeSlug: catalog.slug };
    const existing = base.lines.find((line) => line.id === key);
    const next: Cart = {
      ...base,
      lines: existing
        ? base.lines.map((line) =>
            line.id === key ? { ...line, quantity: line.quantity + quantity } : line,
          )
        : [
            ...base.lines,
            {
              id: key,
              itemId: item.id,
              modifierOptionIds: selected,
              name: item.name,
              quantity,
              unitPriceMillimes: item.priceMillimes + modifierTotal,
            },
          ],
    };
    saveCart(next);
    setCart(next);
  };

  const updateQuantity = (lineId: string, delta: number) => {
    if (!cart) return;
    const lines = cart.lines
      .map((line) => (line.id === lineId ? { ...line, quantity: line.quantity + delta } : line))
      .filter((line) => line.quantity > 0);
    const next = lines.length ? { ...cart, lines } : null;
    saveCart(next);
    setCart(next);
    setQuote(null);
  };

  const toggleFavorite = (storeId: string) => {
    const favorite = toggleFavoriteLocal(storeId);
    setFavorites(readFavorites());
    if (!authenticated) return;
    const request = favorite ? api.addFavorite(storeId) : api.removeFavorite(storeId);
    request.catch(() => recordPendingFavoriteOp({ favorite, storeId }));
  };

  const beginCheckout = async (target: Cart | null = cart) => {
    if (!target || !online) return;
    if (!authenticated) {
      setAfterAuth("checkout");
      setScreen("auth");
      return;
    }
    let selected = address;
    if (!selected) selected = await loadAddresses();
    setScreen("checkout");
    if (selected) await createQuote(selected, target);
  };

  const createQuote = async (selectedAddress: CustomerAddress, target: Cart | null = cart) => {
    if (!target || !online) return;
    try {
      setBusy(true);
      setQuote(
        await api.quote(target, selectedAddress, locale, `customer-quote-${crypto.randomUUID()}`),
      );
    } catch (error) {
      setMessage(errorText(error, locale));
    } finally {
      setBusy(false);
    }
  };

  const reorder = async (order: CustomerOrder) => {
    if (!online) return;
    setBusy(true);
    setMessage(null);
    try {
      const detail = await api.order(order.id);
      let store = stores.find((entry) => entry.id === detail.storeId);
      if (!store) {
        const page = await api.stores(locale, currentLocation, "", "");
        store = page.items.find((entry) => entry.id === detail.storeId);
      }
      if (!store) {
        setMessage(tr(locale, "reorderUnavailable"));
        return;
      }
      const content = await api.catalog(store.slug, locale);
      const available = new Map(
        content.categories.flatMap((entry) => entry.items).map((item) => [item.id, item]),
      );
      const lines: Cart["lines"] = [];
      let dropped = false;
      for (const item of detail.items ?? []) {
        const current = available.get(item.catalogItemId);
        if (!current?.available) {
          dropped = true;
          continue;
        }
        const validOptions = new Set(
          current.modifierGroups.flatMap((group) => group.options).map((option) => option.id),
        );
        const modifiers = item.modifiers
          .map((modifier) => modifier.optionId)
          .filter((optionId) => validOptions.has(optionId));
        if (modifiers.length !== item.modifiers.length) dropped = true;
        lines.push({
          id: `${current.id}:${[...modifiers].sort().join(",")}`,
          itemId: current.id,
          modifierOptionIds: modifiers,
          name: current.name,
          quantity: item.quantity,
          unitPriceMillimes: current.priceMillimes + modifierTotal(current, modifiers),
        });
      }
      if (!lines.length) {
        setMessage(tr(locale, "reorderUnavailable"));
        return;
      }
      const next: Cart = {
        lines,
        storeId: store.id,
        storeName: content.name,
        storeSlug: content.slug,
      };
      saveCart(next);
      setCart(next);
      setQuote(null);
      if (dropped) setMessage(tr(locale, "reorderPartial"));
      else if (!content.isOpen || !content.acceptingOrders) setMessage(tr(locale, "reorderClosed"));
      await beginCheckout(next);
    } catch (error) {
      setMessage(errorText(error, locale));
    } finally {
      setBusy(false);
    }
  };

  const afterLogin = async () => {
    setAuthenticated(true);
    const selected = await loadAddresses();
    setScreen(afterAuth);
    if (afterAuth === "checkout" && selected) await createQuote(selected);
    if (afterAuth === "orders") await refreshOrders();
  };

  const orderPlaced = (order: CustomerOrder) => {
    saveCart(null);
    setCart(null);
    setQuote(null);
    setActiveOrder(order);
    setScreen("tracking");
    setTab("orders");
  };

  const shellless = screen === "auth";
  return (
    <div className="customer-app">
      {!shellless && (
        <Header
          address={address}
          cartCount={cart?.lines.reduce((sum, line) => sum + line.quantity, 0) ?? 0}
          locale={locale}
          online={online}
          setLocale={(next) => {
            setLocale(next);
            void loadStores(next);
          }}
          showCart={() => void beginCheckout()}
          showNotifications={authenticated ? () => setScreen("notifications") : null}
          unreadNotifications={unreadNotifications}
        />
      )}
      {!online && (
        <div className="offline-banner">
          <Icon name="wallet" />
          {tr(locale, "offline")}
        </div>
      )}
      {cached && <div className="cache-banner">{tr(locale, "cached")}</div>}
      {message && (
        <div className="toast" role="alert">
          <span>{message}</span>
          <button type="button" onClick={() => setMessage(null)}>
            ×
          </button>
        </div>
      )}
      <main className={shellless ? "auth-main" : "main-content"}>
        {screen === "home" && (
          <HomeScreen
            busy={busy}
            category={category}
            favorites={favorites}
            favoritesOnly={tab === "favorites"}
            locale={locale}
            openStore={openStore}
            search={search}
            setCategory={(value) => {
              setCategory(value);
              void loadStores(locale, value, search);
            }}
            setSearch={setSearch}
            stores={stores}
            submitSearch={() => void loadStores(locale, category, search)}
            toggleFavorite={toggleFavorite}
          />
        )}
        {screen === "store" && catalog && (
          <StoreScreen
            addLine={addLine}
            back={() => setScreen("home")}
            cart={cart}
            catalog={catalog}
            locale={locale}
            openCart={() => void beginCheckout()}
          />
        )}
        {screen === "checkout" && (
          <CheckoutScreen
            address={address}
            addresses={addresses}
            api={api}
            busy={busy}
            cart={cart}
            locale={locale}
            online={online}
            quote={quote}
            refreshQuote={createQuote}
            saveAddress={async (input) => {
              const saved = await api.createAddress(input);
              setAddress(saved);
              setAddresses((current) => [
                saved,
                ...current.map((entry) => ({ ...entry, isDefault: false })),
              ]);
              await createQuote(saved);
            }}
            selectAddress={(entry) => {
              setAddress(entry);
              void createQuote(entry);
            }}
            setBusy={setBusy}
            setMessage={setMessage}
            updateQuantity={updateQuantity}
            onPlaced={orderPlaced}
          />
        )}
        {screen === "orders" && (
          <OrdersScreen
            busy={busy}
            locale={locale}
            openOrder={(order) => {
              setActiveOrder(order);
              setScreen("tracking");
            }}
            orders={orders}
            reorder={(order) => void reorder(order)}
          />
        )}
        {screen === "notifications" && (
          <NotificationsScreen
            api={api}
            back={() => setScreen("home")}
            locale={locale}
            online={online}
            setUnread={setUnreadNotifications}
          />
        )}
        {screen === "tracking" && activeOrder && (
          <TrackingScreen
            api={api}
            locale={locale}
            online={online}
            order={activeOrder}
            setMessage={setMessage}
            setOrder={setActiveOrder}
            snapshot={tracking}
          />
        )}
        {screen === "profile" && (
          <ProfileScreen
            addresses={addresses}
            api={api}
            locale={locale}
            logout={async () => {
              await api.logout();
              setAuthenticated(false);
              setScreen("home");
              setTab("home");
            }}
            online={online}
            setAddresses={setAddresses}
            setMessage={setMessage}
          />
        )}
        {screen === "auth" && (
          <AuthScreen
            api={api}
            locale={locale}
            onDone={() => void afterLogin()}
            setLocale={setLocale}
          />
        )}
      </main>
      {!shellless && (
        <BottomNav active={tab} locale={locale} navigate={(next) => void navigateTab(next)} />
      )}
    </div>
  );
}

function Header(props: {
  address: CustomerAddress | null;
  cartCount: number;
  locale: Locale;
  online: boolean;
  setLocale: (locale: Locale) => void;
  showCart: () => void;
  showNotifications: (() => void) | null;
  unreadNotifications: number;
}) {
  return (
    <header className="topbar">
      <Brand light />
      <button type="button" className="address-pill" aria-label={tr(props.locale, "address")}>
        <Icon name="pin" />
        <span>
          <small>{tr(props.locale, "address")}</small>
          <b>
            {props.address?.displayLabel ?? (props.locale === "ar" ? "تونس الكبرى" : "Grand Tunis")}
          </b>
        </span>
      </button>
      <div className="top-actions">
        <button
          type="button"
          className="locale-button"
          onClick={() => props.setLocale(props.locale === "ar" ? "fr" : "ar")}
        >
          {props.locale === "ar" ? "FR" : "عر"}
        </button>
        {props.showNotifications && (
          <button
            type="button"
            className="cart-button"
            onClick={props.showNotifications}
            aria-label={tr(props.locale, "notifications")}
          >
            <Icon name="bell" />
            {props.unreadNotifications > 0 && <b>{Math.min(props.unreadNotifications, 99)}</b>}
          </button>
        )}
        <button
          type="button"
          className="cart-button"
          onClick={props.showCart}
          disabled={!props.online}
          aria-label={tr(props.locale, "cart")}
        >
          <Icon name="bag" />
          {props.cartCount > 0 && <b>{props.cartCount}</b>}
        </button>
      </div>
    </header>
  );
}

function HomeScreen(props: {
  busy: boolean;
  category: BusinessType | "";
  favorites: ReadonlySet<string>;
  favoritesOnly: boolean;
  locale: Locale;
  openStore: (store: StoreSummary) => void;
  search: string;
  setCategory: (category: BusinessType | "") => void;
  setSearch: (search: string) => void;
  stores: StoreSummary[];
  submitSearch: () => void;
  toggleFavorite: (storeId: string) => void;
}) {
  const visible = props.favoritesOnly
    ? props.stores.filter((store) => props.favorites.has(store.id))
    : props.stores;
  const categories: Array<{ icon: IconName; key: BusinessType | ""; label: string }> = [
    { icon: "shop", key: "", label: tr(props.locale, "all") },
    { icon: "food", key: "restaurant", label: tr(props.locale, "restaurant") },
    { icon: "bag", key: "grocery", label: tr(props.locale, "grocery") },
    { icon: "pharmacy", key: "pharmacy", label: tr(props.locale, "pharmacy") },
    { icon: "shop", key: "shop", label: tr(props.locale, "shop") },
  ];
  return (
    <>
      <section className="hero-band">
        <div>
          <span className="eyebrow">GRAND TUNIS · LIVE MARKET</span>
          <h1>{props.locale === "ar" ? "شنوّة تشتهي اليوم؟" : "Une envie maintenant ?"}</h1>
          <p>
            {props.locale === "ar"
              ? "المتجر، السعر، والمعلوم واضحون قبل ما تأكد."
              : "Le magasin, le prix et les frais sont clairs avant confirmation."}
          </p>
        </div>
        <form
          className="search-box"
          onSubmit={(event) => {
            event.preventDefault();
            props.submitSearch();
          }}
        >
          <Icon name="search" />
          <input
            id="discovery-search"
            value={props.search}
            onChange={(event) => props.setSearch(event.target.value)}
            placeholder={tr(props.locale, "search")}
          />
          <button type="submit">{props.locale === "ar" ? "ابحث" : "Chercher"}</button>
        </form>
      </section>
      <section
        className="category-strip"
        aria-label={props.locale === "ar" ? "التصنيفات" : "Catégories"}
      >
        {categories.map((entry) => (
          <button
            type="button"
            className={props.category === entry.key ? "active" : ""}
            key={entry.key || "all"}
            onClick={() => props.setCategory(entry.key)}
          >
            <span>
              <Icon name={entry.icon} />
            </span>
            {entry.label}
          </button>
        ))}
      </section>
      <section className="discovery-section">
        <div className="section-heading">
          <div>
            <span className="eyebrow">SERVICEABLE NOW</span>
            <h2>
              {props.favoritesOnly
                ? tr(props.locale, "favorites")
                : props.locale === "ar"
                  ? "قريب منك"
                  : "Près de vous"}
            </h2>
          </div>
          <b>{visible.length}</b>
        </div>
        {props.busy ? (
          <StoreSkeleton />
        ) : visible.length ? (
          <div className="store-grid">
            {visible.map((store) => (
              <StoreCard
                favorite={props.favorites.has(store.id)}
                key={store.id}
                locale={props.locale}
                open={() => props.openStore(store)}
                store={store}
                toggleFavorite={() => props.toggleFavorite(store.id)}
              />
            ))}
          </div>
        ) : (
          <EmptyState icon="search" title={tr(props.locale, "noResults")} />
        )}
      </section>
    </>
  );
}

function StoreCard({
  favorite,
  locale,
  open,
  store,
  toggleFavorite,
}: {
  favorite: boolean;
  locale: Locale;
  open: () => void;
  store: StoreSummary;
  toggleFavorite: () => void;
}) {
  return (
    <article className={`store-card ${store.isOpen && store.acceptingOrders ? "" : "closed"}`}>
      <button type="button" className="store-open" onClick={open} disabled={!store.acceptingOrders}>
        <span className="store-monogram">{store.name.slice(0, 1)}</span>
        <span className="store-copy">
          <span className="store-type">{typeLabel(store.businessType, locale)}</span>
          <b>{store.name}</b>
          <small>{store.tags.slice(0, 3).join(" · ") || store.description}</small>
          <span className="store-meta">
            <i className={store.isOpen ? "open" : ""} />
            {store.isOpen ? tr(locale, "open") : tr(locale, "closed")}
          </span>
        </span>
        <Icon name="chevron" className="row-chevron" />
      </button>
      <button
        type="button"
        className={`favorite-button ${favorite ? "active" : ""}`}
        aria-label={tr(locale, "favorites")}
        aria-pressed={favorite}
        onClick={toggleFavorite}
      >
        <Icon name="heart" />
      </button>
    </article>
  );
}

function StoreScreen(props: {
  addLine: (item: CatalogItem, selected: string[], quantity: number) => void;
  back: () => void;
  cart: Cart | null;
  catalog: CustomerCatalog;
  locale: Locale;
  openCart: () => void;
}) {
  const [activeCategory, setActiveCategory] = useState(props.catalog.categories[0]?.id ?? "");
  const [selectedItem, setSelectedItem] = useState<CatalogItem | null>(null);
  return (
    <div className="store-page">
      <section className="store-hero">
        <button type="button" className="round-back" onClick={props.back}>
          <Icon name="back" />
        </button>
        <span className="store-hero-letter">{props.catalog.name.slice(0, 1)}</span>
        <div>
          <span className="eyebrow">{props.catalog.tags.join(" · ")}</span>
          <h1>{props.catalog.name}</h1>
          <p>{props.catalog.description}</p>
          <span className={`open-badge ${props.catalog.isOpen ? "" : "closed"}`}>
            {props.catalog.isOpen ? tr(props.locale, "open") : tr(props.locale, "closed")}
          </span>
        </div>
      </section>
      <nav className="menu-tabs">
        {props.catalog.categories.map((category) => (
          <button
            type="button"
            className={activeCategory === category.id ? "active" : ""}
            key={category.id}
            onClick={() => {
              setActiveCategory(category.id);
              document
                .getElementById(`category-${category.id}`)
                ?.scrollIntoView({ behavior: "smooth" });
            }}
          >
            {category.name}
          </button>
        ))}
      </nav>
      <div className="menu-layout">
        <div className="menu-list">
          {props.catalog.categories.map((category) => (
            <section id={`category-${category.id}`} key={category.id}>
              <h2>{category.name}</h2>
              {category.items.map((item) => (
                <MenuItem
                  item={item}
                  key={item.id}
                  locale={props.locale}
                  select={() => setSelectedItem(item)}
                />
              ))}
            </section>
          ))}
        </div>
        <aside className="desktop-cart">
          <MiniCart cart={props.cart} locale={props.locale} open={props.openCart} />
        </aside>
      </div>
      {props.cart && (
        <button type="button" className="floating-cart" onClick={props.openCart}>
          <span>
            <Icon name="bag" />
            {props.cart.lines.reduce((sum, line) => sum + line.quantity, 0)}
          </span>
          <b>{tr(props.locale, "checkout")}</b>
          <strong>{money(cartSubtotal(props.cart), props.locale)}</strong>
        </button>
      )}
      {selectedItem && (
        <ModifierSheet
          item={selectedItem}
          locale={props.locale}
          close={() => setSelectedItem(null)}
          add={(selected, quantity) => {
            props.addLine(selectedItem, selected, quantity);
            setSelectedItem(null);
          }}
        />
      )}
    </div>
  );
}

function MenuItem({
  item,
  locale,
  select,
}: {
  item: CatalogItem;
  locale: Locale;
  select: () => void;
}) {
  return (
    <button type="button" className="menu-item" disabled={!item.available} onClick={select}>
      <span className="item-monogram">{item.name.slice(0, 1)}</span>
      <span>
        <b>{item.name}</b>
        <small>{item.description}</small>
        <strong>{money(item.priceMillimes, locale)}</strong>
      </span>
      <span className="add-circle">
        <Icon name={item.available ? "plus" : "clock"} />
      </span>
    </button>
  );
}

function ModifierSheet({
  add,
  close,
  item,
  locale,
}: {
  add: (selected: string[], quantity: number) => void;
  close: () => void;
  item: CatalogItem;
  locale: Locale;
}) {
  const [selected, setSelected] = useState<string[]>([]);
  const [quantity, setQuantity] = useState(1);
  const valid = item.modifierGroups.every((group) => {
    const count = group.options.filter((option) => selected.includes(option.id)).length;
    return count >= group.minSelections && count <= group.maxSelections;
  });
  const toggle = (group: ModifierGroup, id: string) =>
    setSelected((current) => {
      if (current.includes(id)) return current.filter((entry) => entry !== id);
      const inGroup = group.options.filter((option) => current.includes(option.id));
      return group.maxSelections === 1 || inGroup.length >= group.maxSelections
        ? [...current.filter((entry) => !group.options.some((option) => option.id === entry)), id]
        : [...current, id];
    });
  return (
    <div className="sheet-backdrop">
      <button
        type="button"
        className="sheet-close-layer"
        aria-label={locale === "ar" ? "إغلاق" : "Fermer"}
        onClick={close}
      />
      <section
        className="modifier-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modifier-title"
      >
        <header>
          <div>
            <span className="eyebrow">CUSTOMIZE</span>
            <h2 id="modifier-title">{item.name}</h2>
          </div>
          <button type="button" onClick={close}>
            ×
          </button>
        </header>
        {item.modifierGroups.map((group) => (
          <fieldset key={group.id}>
            <legend>
              {group.name}
              <small>
                {group.minSelections
                  ? locale === "ar"
                    ? "مطلوب"
                    : "Requis"
                  : locale === "ar"
                    ? "اختياري"
                    : "Optionnel"}
              </small>
            </legend>
            {group.options.map((option) => (
              <label key={option.id}>
                <input
                  type={group.maxSelections === 1 ? "radio" : "checkbox"}
                  checked={selected.includes(option.id)}
                  onChange={() => toggle(group, option.id)}
                  name={group.id}
                />
                <span>{option.name}</span>
                <b>
                  {option.priceDeltaMillimes ? `+ ${money(option.priceDeltaMillimes, locale)}` : ""}
                </b>
              </label>
            ))}
          </fieldset>
        ))}
        <footer>
          <Quantity value={quantity} update={setQuantity} />
          <button
            type="button"
            className="primary-button"
            disabled={!valid}
            onClick={() => add(selected, quantity)}
          >
            {tr(locale, "add")} ·{" "}
            {money((item.priceMillimes + modifierTotal(item, selected)) * quantity, locale)}
          </button>
        </footer>
      </section>
    </div>
  );
}

function MiniCart({ cart, locale, open }: { cart: Cart | null; locale: Locale; open: () => void }) {
  return (
    <section className="mini-cart">
      <span className="eyebrow">YOUR RECEIPT</span>
      <h3>{tr(locale, "cart")}</h3>
      {cart?.lines.length ? (
        <>
          {cart.lines.map((line) => (
            <div key={line.id}>
              <span>
                {line.quantity} × {line.name}
              </span>
              <b>{money(line.quantity * line.unitPriceMillimes, locale)}</b>
            </div>
          ))}
          <footer>
            <span>{tr(locale, "total")}</span>
            <b>{money(cartSubtotal(cart), locale)}</b>
          </footer>
          <button type="button" className="primary-button" onClick={open}>
            {tr(locale, "checkout")}
          </button>
        </>
      ) : (
        <p>
          {locale === "ar"
            ? "أضف ما يعجبك، والحساب يظهر هنا."
            : "Ajoutez vos envies; le reçu apparaît ici."}
        </p>
      )}
    </section>
  );
}

function CheckoutScreen(props: {
  address: CustomerAddress | null;
  addresses: CustomerAddress[];
  api: CustomerApi;
  busy: boolean;
  cart: Cart | null;
  locale: Locale;
  online: boolean;
  onPlaced: (order: CustomerOrder) => void;
  quote: Quote | null;
  refreshQuote: (address: CustomerAddress) => Promise<void>;
  saveAddress: (input: CustomerAddressCreate) => Promise<void>;
  selectAddress: (address: CustomerAddress) => void;
  setBusy: (busy: boolean) => void;
  setMessage: (message: string | null) => void;
  updateQuantity: (id: string, delta: number) => void;
}) {
  const [payment, setPayment] = useState<"cash_on_delivery" | "sandbox_card">("cash_on_delivery");
  const [note, setNote] = useState("");
  const [addingAddress, setAddingAddress] = useState(!props.address);
  if (!props.cart)
    return (
      <EmptyState
        icon="bag"
        title={props.locale === "ar" ? "سلتك فارغة" : "Votre panier est vide"}
      />
    );
  const place = async () => {
    if (!props.quote || !props.address || !props.online) return;
    try {
      props.setBusy(true);
      const order = await props.api.placeOrder(
        props.quote.id,
        props.address.addressLine,
        payment,
        note.trim() || null,
        `customer-order-${crypto.randomUUID()}`,
      );
      if (payment === "sandbox_card" && order.payment?.checkoutUrl)
        window.location.assign(order.payment.checkoutUrl);
      props.onPlaced(order);
    } catch (error) {
      props.setMessage(errorText(error, props.locale));
    } finally {
      props.setBusy(false);
    }
  };
  return (
    <div className="checkout-layout">
      <section className="checkout-main">
        <div className="page-title">
          <span className="eyebrow">CONFIRM WITH CLARITY</span>
          <h1>{tr(props.locale, "checkout")}</h1>
          <p>
            {props.locale === "ar"
              ? "لن يتغير أي مبلغ بعد هذه الشاشة دون موافقتك."
              : "Aucun montant ne change après cet écran sans votre accord."}
          </p>
        </div>
        <section className="checkout-card">
          <header>
            <span className="step-number">1</span>
            <div>
              <b>{tr(props.locale, "address")}</b>
              <small>
                {props.locale === "ar"
                  ? "نستعمل الموقع لحساب التوصيل"
                  : "Utilisée pour calculer la livraison"}
              </small>
            </div>
          </header>
          {props.addresses.length > 0 && (
            <div className="address-options">
              {props.addresses.map((entry) => (
                <button
                  type="button"
                  className={props.address?.id === entry.id ? "active" : ""}
                  key={entry.id}
                  onClick={() => props.selectAddress(entry)}
                >
                  <Icon name="pin" />
                  <span>
                    <b>{entry.displayLabel}</b>
                    <small>{entry.addressLine}</small>
                  </span>
                  <Icon name="check" />
                </button>
              ))}
            </div>
          )}
          <button
            type="button"
            className="text-button"
            onClick={() => setAddingAddress(!addingAddress)}
          >
            {props.locale === "ar" ? "+ عنوان جديد" : "+ Nouvelle adresse"}
          </button>
          {addingAddress && (
            <AddressForm
              locale={props.locale}
              online={props.online}
              save={async (input) => {
                await props.saveAddress(input);
                setAddingAddress(false);
              }}
            />
          )}
        </section>
        <section className="checkout-card">
          <header>
            <span className="step-number">2</span>
            <div>
              <b>{props.locale === "ar" ? "محتوى الطلب" : "Votre commande"}</b>
              <small>{props.cart.storeName}</small>
            </div>
          </header>
          <div className="checkout-lines">
            {props.cart.lines.map((line) => (
              <div key={line.id}>
                <span>
                  <b>{line.name}</b>
                  <small>
                    {money(line.unitPriceMillimes, props.locale)} /{" "}
                    {props.locale === "ar" ? "قطعة" : "unité"}
                  </small>
                </span>
                <Quantity
                  value={line.quantity}
                  update={(value) => props.updateQuantity(line.id, value - line.quantity)}
                />
                <strong>{money(line.quantity * line.unitPriceMillimes, props.locale)}</strong>
              </div>
            ))}
          </div>
          <label className="note-field">
            <span>{props.locale === "ar" ? "ملاحظة للمطعم" : "Note au restaurant"}</span>
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              maxLength={500}
              placeholder={props.locale === "ar" ? "مثال: بلا هريسة" : "Ex. sans sauce piquante"}
            />
          </label>
        </section>
        <section className="checkout-card">
          <header>
            <span className="step-number">3</span>
            <div>
              <b>{props.locale === "ar" ? "طريقة الدفع" : "Paiement"}</b>
              <small>
                {props.locale === "ar" ? "لا نخزّن بيانات البطاقة" : "Aucune donnée carte stockée"}
              </small>
            </div>
          </header>
          <div className="payment-options">
            <label className={payment === "cash_on_delivery" ? "active" : ""}>
              <input
                type="radio"
                name="payment"
                checked={payment === "cash_on_delivery"}
                onChange={() => setPayment("cash_on_delivery")}
              />
              <Icon name="cash" />
              <span>
                <b>{props.locale === "ar" ? "الدفع عند التسليم" : "Paiement à la livraison"}</b>
                <small>{props.locale === "ar" ? "نقدًا للسائق" : "En espèces au livreur"}</small>
              </span>
              <Icon name="check" />
            </label>
            <label className={payment === "sandbox_card" ? "active" : ""}>
              <input
                type="radio"
                name="payment"
                checked={payment === "sandbox_card"}
                onChange={() => setPayment("sandbox_card")}
              />
              <Icon name="wallet" />
              <span>
                <b>{props.locale === "ar" ? "بطاقة إلكترونية" : "Carte en ligne"}</b>
                <small>
                  {props.locale === "ar" ? "عبر صفحة دفع آمنة" : "Via une page sécurisée"}
                </small>
              </span>
              <Icon name="check" />
            </label>
          </div>
        </section>
      </section>
      <aside className="receipt-panel">
        <span className="eyebrow">FINAL RECEIPT</span>
        <h2>{props.locale === "ar" ? "الحساب النهائي" : "Reçu final"}</h2>
        {props.quote ? (
          <Receipt quote={props.quote} locale={props.locale} />
        ) : (
          <div className="quote-placeholder">
            {props.address
              ? props.busy
                ? "…"
                : props.locale === "ar"
                  ? "نعيد حساب السعر"
                  : "Recalcul du prix"
              : props.locale === "ar"
                ? "أضف عنوانًا لعرض السعر"
                : "Ajoutez une adresse"}
          </div>
        )}
        <button
          type="button"
          className="primary-button place-order"
          disabled={!props.quote || !props.address || props.busy || !props.online}
          onClick={() => void place()}
        >
          {props.busy ? "…" : tr(props.locale, "placeOrder")}
        </button>
        <p className="receipt-trust">
          <Icon name="check" />
          {props.locale === "ar"
            ? "لا توجد رسوم مخفية. السعر مثبت حتى انتهاء العرض."
            : "Aucun frais caché. Prix figé jusqu’à expiration."}
        </p>
      </aside>
    </div>
  );
}

function AddressForm({
  locale,
  online,
  save,
}: {
  locale: Locale;
  online: boolean;
  save: (input: CustomerAddressCreate) => Promise<void>;
}) {
  const [label, setLabel] = useState("");
  const [line, setLine] = useState("");
  const [location, setLocation] = useState(readGuestLocation());
  const [busy, setBusy] = useState(false);
  const locate = () =>
    navigator.geolocation?.getCurrentPosition((position) => {
      const next = { latitude: position.coords.latitude, longitude: position.coords.longitude };
      setLocation(next);
      localStorage.setItem("gobro-guest-location", JSON.stringify(next));
    });
  return (
    <form
      className="address-form"
      onSubmit={(event) => {
        event.preventDefault();
        setBusy(true);
        void save({
          addressLine: line,
          displayLabel: label,
          instructions: null,
          isDefault: true,
          label: "home",
          location,
        }).finally(() => setBusy(false));
      }}
    >
      <div>
        <label>
          <span>{locale === "ar" ? "اسم العنوان" : "Nom"}</span>
          <input
            required
            minLength={2}
            maxLength={60}
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder={locale === "ar" ? "المنزل" : "Maison"}
          />
        </label>
        <label>
          <span>{locale === "ar" ? "العنوان بالتفصيل" : "Adresse détaillée"}</span>
          <input
            required
            minLength={5}
            maxLength={500}
            value={line}
            onChange={(event) => setLine(event.target.value)}
            placeholder={locale === "ar" ? "الشارع، العدد، المدينة" : "Rue, numéro, ville"}
          />
        </label>
      </div>
      <button type="button" className="location-button" onClick={locate}>
        <Icon name="pin" />
        {tr(locale, "locate")}
        <small>
          {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
        </small>
      </button>
      <button type="submit" className="primary-button" disabled={!online || busy}>
        {locale === "ar" ? "احفظ واستعمل" : "Enregistrer"}
      </button>
    </form>
  );
}

function Receipt({ locale, quote }: { locale: Locale; quote: Quote }) {
  const rows = [
    { label: locale === "ar" ? "قيمة المنتجات" : "Articles", value: quote.money.subtotalMillimes },
    { label: locale === "ar" ? "التوصيل" : "Livraison", value: quote.money.deliveryChargeMillimes },
  ];
  return (
    <div className="receipt-lines">
      {rows.map((row) => (
        <div key={row.label}>
          <span>{row.label}</span>
          <b>{money(row.value, locale)}</b>
        </div>
      ))}
      <div className="waiver-row">
        <span>{locale === "ar" ? "رسوم المنصة عليك" : "Frais plateforme client"}</span>
        <b>{money(0, locale)}</b>
      </div>
      <div className="receipt-total">
        <span>{tr(locale, "total")}</span>
        <strong>{money(quote.money.customerTotalMillimes, locale)}</strong>
      </div>
      <small>
        {locale === "ar"
          ? `صالح حتى ${new Date(quote.expiresAt).toLocaleTimeString("ar-TN-u-nu-latn", { hour: "2-digit", minute: "2-digit" })}`
          : `Valable jusqu’à ${new Date(quote.expiresAt).toLocaleTimeString("fr-TN", { hour: "2-digit", minute: "2-digit" })}`}
      </small>
    </div>
  );
}

function OrdersScreen({
  busy,
  locale,
  openOrder,
  orders,
  reorder,
}: {
  busy: boolean;
  locale: Locale;
  openOrder: (order: CustomerOrder) => void;
  orders: CustomerOrder[];
  reorder: (order: CustomerOrder) => void;
}) {
  return (
    <div className="orders-page">
      <div className="page-title">
        <span className="eyebrow">ORDER HISTORY</span>
        <h1>{tr(locale, "orders")}</h1>
        <p>
          {locale === "ar"
            ? "كل طلب وحالته ومبلغه في مكان واحد."
            : "Statut, montant et suivi au même endroit."}
        </p>
      </div>
      {busy ? (
        <StoreSkeleton />
      ) : orders.length ? (
        <div className="order-list">
          {orders.map((order) => (
            <div className="order-row" key={order.id}>
              <button type="button" className="order-open" onClick={() => openOrder(order)}>
                <span className={`order-status status-${order.status}`}>
                  <Icon
                    name={
                      order.status === "delivered" ||
                      order.status === "cancelled" ||
                      order.status === "refunded"
                        ? "check"
                        : "clock"
                    }
                  />
                </span>
                <span>
                  <b>#{order.publicReference}</b>
                  <small>
                    {statusLabel(order.status, locale)} · {shortDate(order.createdAt, locale)}
                  </small>
                </span>
                <strong>
                  {money(
                    order.customerTotalMillimes ?? order.money?.customerTotalMillimes ?? 0,
                    locale,
                  )}
                </strong>
                <Icon name="chevron" />
              </button>
              <button
                type="button"
                className="reorder-button"
                disabled={busy}
                onClick={() => reorder(order)}
              >
                <Icon name="repeat" />
                {tr(locale, "reorder")}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState icon="orders" title={locale === "ar" ? "لا طلبات بعد" : "Aucune commande"} />
      )}
    </div>
  );
}

function TrackingScreen({
  api,
  locale,
  online,
  order,
  setMessage,
  setOrder,
  snapshot,
}: {
  api: CustomerApi;
  locale: Locale;
  online: boolean;
  order: CustomerOrder;
  setMessage: (message: string | null) => void;
  setOrder: (order: CustomerOrder) => void;
  snapshot: TrackingSnapshot | null;
}) {
  const [support, setSupport] = useState(false);
  const stages = [
    "placed",
    "accepted",
    "preparing",
    "ready_for_pickup",
    "courier_assigned",
    "picked_up",
    "delivered",
  ];
  const terminal = order.status === "cancelled" || order.status === "refunded";
  const progress = Math.max(0, stages.indexOf(order.status));
  const cancel = async () => {
    try {
      setOrder(await api.cancelOrder(order.id, order.version, "customer_changed_mind"));
    } catch (error) {
      setMessage(errorText(error, locale));
    }
  };
  return (
    <div className="tracking-page">
      <section className="tracking-head">
        <span className="eyebrow">ORDER #{order.publicReference}</span>
        <h1>{statusLabel(order.status, locale)}</h1>
        <p>
          {order.status === "cancelled" || order.status === "refunded"
            ? locale === "ar"
              ? "هذه الحالة نهائية ومحفوظة في سجل طلباتك."
              : "Ce statut est définitif et conservé dans votre historique."
            : order.status === "picked_up"
              ? locale === "ar"
                ? "السائق في طريقه إليك الآن."
                : "Votre livreur est en route."
              : locale === "ar"
                ? "نحدّث الحالة تلقائيًا، ويمكنك الرجوع في أي وقت."
                : "Le statut se met à jour automatiquement."}
        </p>
      </section>
      <div className="tracking-grid">
        <section className="map-card">
          {terminal ? (
            <div className={`terminal-order-state terminal-${order.status}`}>
              <span>
                <Icon name={order.status === "refunded" ? "wallet" : "orders"} />
              </span>
              <h2>
                {order.status === "refunded"
                  ? locale === "ar"
                    ? "تمت التسوية"
                    : "Règlement terminé"
                  : locale === "ar"
                    ? "التتبّع مغلق"
                    : "Suivi clôturé"}
              </h2>
              <p>
                {order.status === "refunded"
                  ? locale === "ar"
                    ? "اكتمل إرجاع المبلغ، ولا يوجد تتبّع نشط لهذا الطلب."
                    : "Le remboursement est terminé et le suivi est clôturé."
                  : locale === "ar"
                    ? "أُلغي الطلب وأُغلق التتبّع. يمكنك مراجعة التفاصيل أو طلب المساعدة."
                    : "La commande est annulée et le suivi est clôturé."}
              </p>
              {order.status === "refunded" && order.payment && (
                <b>{money(order.payment.refundedMillimes, locale)}</b>
              )}
            </div>
          ) : (
            <>
              <TrackingMap snapshot={snapshot} locale={locale} />
              <div className="eta-strip">
                <span>
                  <Icon name="clock" />
                  <small>{locale === "ar" ? "الوصول المتوقع" : "Arrivée estimée"}</small>
                  <b>
                    {snapshot?.route
                      ? `${Math.max(1, Math.ceil(snapshot.route.durationRemainingSeconds / 60))} ${locale === "ar" ? "دق" : "min"}`
                      : "—"}
                  </b>
                </span>
                {snapshot?.route && (
                  <span>
                    <small>{locale === "ar" ? "المسافة" : "Distance"}</small>
                    <b>{distance(snapshot.route.distanceRemainingMeters)}</b>
                  </span>
                )}
              </div>
            </>
          )}
        </section>
        <aside className="status-card">
          <h2>
            {terminal
              ? locale === "ar"
                ? "ملخص الطلب"
                : "Récapitulatif"
              : locale === "ar"
                ? "مسار الطلب"
                : "Parcours"}
          </h2>
          {!terminal && (
            <ol>
              {stages.map((stage, index) => (
                <li
                  className={index < progress ? "done" : index === progress ? "current" : ""}
                  key={stage}
                >
                  <span>{index < progress ? <Icon name="check" /> : index + 1}</span>
                  <b>{statusLabel(stage, locale)}</b>
                </li>
              ))}
            </ol>
          )}
          {order.money && (
            <Receipt
              quote={{
                expiresAt: order.updatedAt,
                id: order.id,
                money: order.money,
                storeId: order.storeId,
              }}
              locale={locale}
            />
          )}
          {order.status === "placed" && (
            <button
              type="button"
              className="danger-text"
              disabled={!online}
              onClick={() => void cancel()}
            >
              {tr(locale, "cancel")}
            </button>
          )}
          <button type="button" className="support-button" onClick={() => setSupport(!support)}>
            <Icon name="user" />
            {locale === "ar" ? "تحتاج مساعدة؟" : "Besoin d’aide ?"}
          </button>
          {support && (
            <SupportForm
              api={api}
              locale={locale}
              online={online}
              orderId={order.id}
              setMessage={setMessage}
            />
          )}
        </aside>
      </div>
    </div>
  );
}

function TrackingMap({ locale, snapshot }: { locale: Locale; snapshot: TrackingSnapshot | null }) {
  return <LiveTrackingMap locale={locale} snapshot={snapshot} />;
}

function SupportForm({
  api,
  locale,
  online,
  orderId,
  setMessage,
}: {
  api: CustomerApi;
  locale: Locale;
  online: boolean;
  orderId: string;
  setMessage: (message: string | null) => void;
}) {
  const [category, setCategory] = useState<CustomerSupportCategory>("order_issue");
  const [message, setText] = useState("");
  return (
    <form
      className="support-form"
      onSubmit={(event) => {
        event.preventDefault();
        void api
          .createSupport({
            category,
            message,
            orderId,
            subject: locale === "ar" ? "مساعدة بخصوص الطلب" : "Aide commande",
          })
          .then(() => {
            setText("");
            setMessage(locale === "ar" ? "تم إرسال طلب الدعم" : "Demande envoyée");
          })
          .catch((error) => setMessage(errorText(error, locale)));
      }}
    >
      <select
        value={category}
        onChange={(event) => setCategory(event.target.value as CustomerSupportCategory)}
      >
        <option value="order_issue">{locale === "ar" ? "مشكلة في الطلب" : "Commande"}</option>
        <option value="payment_issue">{locale === "ar" ? "الدفع" : "Paiement"}</option>
        <option value="delivery_issue">{locale === "ar" ? "التوصيل" : "Livraison"}</option>
      </select>
      <textarea
        required
        minLength={10}
        maxLength={2000}
        value={message}
        onChange={(event) => setText(event.target.value)}
        placeholder={locale === "ar" ? "اشرح لنا ما حدث..." : "Expliquez la situation…"}
      />
      <button
        type="submit"
        className="primary-button"
        disabled={!online || message.trim().length < 10}
      >
        {locale === "ar" ? "أرسل للدعم" : "Envoyer"}
      </button>
    </form>
  );
}

function NotificationsScreen({
  api,
  back,
  locale,
  online,
  setUnread,
}: {
  api: CustomerApi;
  back: () => void;
  locale: Locale;
  online: boolean;
  setUnread: (count: number) => void;
}) {
  const [items, setItems] = useState<NotificationFeedItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .notificationFeed(null)
      .then((page) => {
        if (cancelled) return;
        setItems(page.items);
        setCursor(page.nextCursor);
        setUnread(page.unreadCount);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, setUnread]);

  const open = async (item: NotificationFeedItem) => {
    if (item.readAt) return;
    try {
      const updated = await api.markNotificationRead(item.id);
      setItems((current) => current.map((entry) => (entry.id === item.id ? updated : entry)));
      setUnread(Math.max(0, items.filter((entry) => !entry.readAt && entry.id !== item.id).length));
    } catch {
      // The unread badge is refreshed by the periodic poll.
    }
  };

  const markAll = async () => {
    try {
      await api.markAllNotificationsRead();
      setItems((current) =>
        current.map((entry) => ({ ...entry, readAt: entry.readAt ?? new Date().toISOString() })),
      );
      setUnread(0);
    } catch {
      // Keep the local list; the next poll reconciles.
    }
  };

  const loadMore = async () => {
    if (!cursor) return;
    const page = await api.notificationFeed(cursor);
    setItems((current) => [...current, ...page.items]);
    setCursor(page.nextCursor);
  };

  return (
    <div className="profile-page">
      <div className="page-title">
        <span className="eyebrow">GOBRO</span>
        <h1>{tr(locale, "notifications")}</h1>
      </div>
      <section className="profile-card">
        <header>
          <div>
            <Icon name="bell" />
            <span>
              <b>{tr(locale, "notifications")}</b>
              <small>{items.filter((entry) => !entry.readAt).length}</small>
            </span>
          </div>
          <button type="button" className="text-button" onClick={() => void markAll()}>
            {tr(locale, "markAllRead")}
          </button>
        </header>
        {loading && <p className="notification-empty">…</p>}
        {!loading && items.length === 0 && (
          <p className="notification-empty">{tr(locale, "noNotifications")}</p>
        )}
        {items.map((item) => (
          <button
            type="button"
            className={`notification-item${item.readAt ? " read" : ""}`}
            key={item.id}
            onClick={() => void open(item)}
          >
            <span>
              <b>{item.title[locale]}</b>
              <small>{item.body[locale]}</small>
            </span>
            <time>{shortDate(item.createdAt, locale)}</time>
          </button>
        ))}
        {cursor && (
          <button
            type="button"
            className="text-button"
            disabled={!online}
            onClick={() => void loadMore()}
          >
            {locale === "ar" ? "المزيد" : "Plus"}
          </button>
        )}
      </section>
      <button type="button" className="text-button" onClick={back}>
        {locale === "ar" ? "رجوع" : "Retour"}
      </button>
    </div>
  );
}

function NotificationPreferencesCard({
  api,
  locale,
  online,
  setMessage,
}: {
  api: CustomerApi;
  locale: Locale;
  online: boolean;
  setMessage: (message: string | null) => void;
}) {
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [pushState, setPushState] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .notificationPreferences()
      .then((value) => {
        if (!cancelled) setPreferences(value);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [api]);

  const save = async (input: Parameters<CustomerApi["updateNotificationPreferences"]>[0]) => {
    try {
      setPreferences(await api.updateNotificationPreferences(input));
    } catch (error) {
      setMessage(errorText(error, locale));
    }
  };

  const enablePush = async () => {
    try {
      const result = await subscribeWebPush(api);
      setPushState(result);
      if (result === "subscribed" && preferences && !preferences.channels.push) {
        await save({ channels: { push: true } });
      }
    } catch (error) {
      setMessage(errorText(error, locale));
    }
  };

  const rows: Array<{
    label: string;
    on: boolean;
    toggle: () => void;
  }> = preferences
    ? [
        {
          label: locale === "ar" ? "إشعارات داخل التطبيق" : "Notifications dans l'application",
          on: preferences.channels.inApp,
          toggle: () => void save({ channels: { inApp: !preferences.channels.inApp } }),
        },
        {
          label: locale === "ar" ? "إشعارات فورية (Push)" : "Notifications push",
          on: preferences.channels.push,
          toggle: () => void save({ channels: { push: !preferences.channels.push } }),
        },
        {
          label: locale === "ar" ? "تحديثات الطلبات" : "Suivi des commandes",
          on: preferences.categories.orders,
          toggle: () => void save({ categories: { orders: !preferences.categories.orders } }),
        },
        {
          label: locale === "ar" ? "العروض والتخفيضات" : "Promotions",
          on: preferences.categories.promos,
          toggle: () => void save({ categories: { promos: !preferences.categories.promos } }),
        },
        {
          label: locale === "ar" ? "رسائل النظام" : "Messages système",
          on: preferences.categories.system,
          toggle: () => void save({ categories: { system: !preferences.categories.system } }),
        },
      ]
    : [];

  return (
    <section className="profile-card">
      <header>
        <div>
          <Icon name="bell" />
          <span>
            <b>{tr(locale, "notifications")}</b>
            <small>
              {pushState === "subscribed"
                ? locale === "ar"
                  ? "مفعّلة على هذا الجهاز"
                  : "Activées sur cet appareil"
                : locale === "ar"
                  ? "تحكم في القنوات والفئات"
                  : "Canaux et catégories"}
            </small>
          </span>
        </div>
        <button
          type="button"
          className="text-button"
          disabled={!online || pushState === "subscribed"}
          onClick={() => void enablePush()}
        >
          {locale === "ar" ? "تفعيل الإشعارات الفورية" : "Activer le push"}
        </button>
      </header>
      {rows.map((row) => (
        <div className="preference-row" key={row.label}>
          <span>{row.label}</span>
          <button
            type="button"
            role="switch"
            aria-checked={row.on}
            aria-label={row.label}
            className={`switch${row.on ? " on" : ""}`}
            disabled={!online}
            onClick={row.toggle}
          >
            <i />
          </button>
        </div>
      ))}
    </section>
  );
}

function ProfileScreen({
  addresses,
  api,
  locale,
  logout,
  online,
  setAddresses,
  setMessage,
}: {
  addresses: CustomerAddress[];
  api: CustomerApi;
  locale: Locale;
  logout: () => Promise<void>;
  online: boolean;
  setAddresses: (addresses: CustomerAddress[]) => void;
  setMessage: (message: string | null) => void;
}) {
  const [adding, setAdding] = useState(false);
  return (
    <div className="profile-page">
      <div className="page-title">
        <span className="eyebrow">PRIVATE BY DESIGN</span>
        <h1>{tr(locale, "account")}</h1>
        <p>
          {locale === "ar"
            ? "عناوينك مشفّرة ولا يراها إلا مسار توصيل طلبك."
            : "Vos adresses sont chiffrées et limitées à votre livraison."}
        </p>
      </div>
      <section className="profile-card">
        <header>
          <div>
            <Icon name="pin" />
            <span>
              <b>{locale === "ar" ? "عناويني" : "Mes adresses"}</b>
              <small>{addresses.length}</small>
            </span>
          </div>
          <button type="button" className="text-button" onClick={() => setAdding(!adding)}>
            + {tr(locale, "add")}
          </button>
        </header>
        {addresses.map((entry) => (
          <div className="saved-address" key={entry.id}>
            <span>
              <b>
                {entry.displayLabel}
                {entry.isDefault && <i>{locale === "ar" ? "افتراضي" : "Défaut"}</i>}
              </b>
              <small>{entry.addressLine}</small>
            </span>
            <button
              type="button"
              aria-label={tr(locale, "cancel")}
              disabled={!online}
              onClick={() =>
                void api
                  .deleteAddress(entry.id)
                  .then(() => setAddresses(addresses.filter((item) => item.id !== entry.id)))
                  .catch((error) => setMessage(errorText(error, locale)))
              }
            >
              ×
            </button>
          </div>
        ))}
        {adding && (
          <AddressForm
            locale={locale}
            online={online}
            save={async (input) => {
              const saved = await api.createAddress(input);
              setAddresses([saved, ...addresses.map((entry) => ({ ...entry, isDefault: false }))]);
              setAdding(false);
            }}
          />
        )}
      </section>
      <NotificationPreferencesCard
        api={api}
        locale={locale}
        online={online}
        setMessage={setMessage}
      />
      <button
        type="button"
        className="logout-button"
        disabled={!online}
        onClick={() => void logout()}
      >
        {locale === "ar" ? "تسجيل الخروج" : "Se déconnecter"}
      </button>
    </div>
  );
}

function AuthScreen({
  api,
  locale,
  onDone,
  setLocale,
}: {
  api: CustomerApi;
  locale: Locale;
  onDone: () => void;
  setLocale: (locale: Locale) => void;
}) {
  const [phone, setPhone] = useState("+216");
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (!challenge) setChallenge((await api.requestOtp(phone)).challengeId);
      else {
        await api.verifyOtp(challenge, code);
        onDone();
      }
    } catch (failure) {
      setError(errorText(failure, locale));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="auth-layout">
      <section className="auth-brand">
        <Brand light />
        <span>INDEPENDENT DELIVERY MARKET</span>
        <div>
          <h1>
            {locale === "ar"
              ? "طلبك واضح من البداية حتى الباب."
              : "Votre commande, claire jusqu’à la porte."}
          </h1>
          <p>
            {locale === "ar"
              ? "بدون رسوم مخفية، وبهوية تونسية مستقلة."
              : "Sans frais cachés, avec une identité tunisienne indépendante."}
          </p>
        </div>
      </section>
      <section className="auth-panel">
        <button
          type="button"
          className="locale-button"
          onClick={() => setLocale(locale === "ar" ? "fr" : "ar")}
        >
          {locale === "ar" ? "FR" : "عر"}
        </button>
        <span className="eyebrow">SECURE PHONE ACCESS</span>
        <h2>
          {challenge
            ? locale === "ar"
              ? "رمز التحقق"
              : "Code de vérification"
            : tr(locale, "login")}
        </h2>
        <p>
          {challenge
            ? locale === "ar"
              ? `أدخل الرمز المرسل إلى ${phone}`
              : `Code envoyé au ${phone}`
            : locale === "ar"
              ? "ندخلّك برقم الهاتف فقط، دون كلمة سر."
              : "Connexion par téléphone, sans mot de passe."}
        </p>
        {error && <div className="form-error">{error}</div>}
        {challenge ? (
          <input
            className="otp-input"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
            aria-label={locale === "ar" ? "رمز التحقق" : "Code"}
          />
        ) : (
          <label className="phone-field">
            <span>{locale === "ar" ? "رقم الهاتف" : "Téléphone"}</span>
            <input
              dir="ltr"
              type="tel"
              autoComplete="tel"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
            />
          </label>
        )}
        <button
          type="button"
          className="primary-button auth-submit"
          disabled={busy || (challenge ? code.length < 4 : phone.length < 12)}
          onClick={() => void submit()}
        >
          {busy
            ? "…"
            : challenge
              ? locale === "ar"
                ? "تأكيد ودخول"
                : "Valider"
              : locale === "ar"
                ? "أرسل الرمز"
                : "Envoyer le code"}
        </button>
        {challenge && (
          <button
            type="button"
            className="text-button"
            onClick={() => {
              setChallenge(null);
              setCode("");
            }}
          >
            {locale === "ar" ? "تغيير الرقم" : "Changer le numéro"}
          </button>
        )}
      </section>
    </div>
  );
}

function BottomNav({
  active,
  locale,
  navigate,
}: {
  active: Tab;
  locale: Locale;
  navigate: (tab: Tab) => void;
}) {
  const entries: Array<{ icon: IconName; label: string; tab: Tab }> = [
    { icon: "home", label: tr(locale, "home"), tab: "home" },
    { icon: "search", label: locale === "ar" ? "اكتشف" : "Explorer", tab: "search" },
    { icon: "orders", label: tr(locale, "orders"), tab: "orders" },
    { icon: "heart", label: tr(locale, "favorites"), tab: "favorites" },
    { icon: "user", label: tr(locale, "account"), tab: "profile" },
  ];
  return (
    <nav
      className="bottom-nav"
      aria-label={locale === "ar" ? "التنقل الرئيسي" : "Navigation principale"}
    >
      {entries.map((entry) => (
        <button
          type="button"
          className={active === entry.tab ? "active" : ""}
          key={entry.tab}
          onClick={() => navigate(entry.tab)}
        >
          <Icon name={entry.icon} />
          <span>{entry.label}</span>
        </button>
      ))}
    </nav>
  );
}

function Quantity({ update, value }: { update: (value: number) => void; value: number }) {
  return (
    <span className="quantity">
      <button type="button" onClick={() => update(Math.max(1, value - 1))}>
        <Icon name="minus" />
      </button>
      <b>{value}</b>
      <button type="button" onClick={() => update(Math.min(99, value + 1))}>
        <Icon name="plus" />
      </button>
    </span>
  );
}
function EmptyState({ icon, title }: { icon: IconName; title: string }) {
  return (
    <div className="empty-state">
      <Icon name={icon} />
      <b>{title}</b>
    </div>
  );
}
function StoreSkeleton() {
  return (
    <div className="store-grid skeleton-grid">
      {[1, 2, 3, 4].map((item) => (
        <div key={item}>
          <span />
          <span />
          <span />
        </div>
      ))}
    </div>
  );
}

function typeLabel(type: BusinessType, locale: Locale) {
  const labels = {
    grocery: tr(locale, "grocery"),
    other: locale === "ar" ? "خدمات" : "Services",
    pharmacy: tr(locale, "pharmacy"),
    restaurant: tr(locale, "restaurant"),
    shop: tr(locale, "shop"),
  };
  return labels[type];
}
function statusLabel(status: string, locale: Locale) {
  const ar: Record<string, string> = {
    accepted: "تم قبول الطلب",
    awaiting_payment: "بانتظار الدفع",
    cancelled: "تم إلغاء الطلب",
    courier_assigned: "تم تعيين السائق",
    delivered: "وصلت، صحة وهنا",
    picked_up: "السائق في الطريق",
    placed: "تم تأكيد الطلب",
    preparing: "قيد التحضير",
    ready_for_pickup: "جاهز للاستلام",
    refunded: "تم استرجاع المبلغ",
  };
  const fr: Record<string, string> = {
    accepted: "Commande acceptée",
    awaiting_payment: "Paiement en attente",
    cancelled: "Commande annulée",
    courier_assigned: "Livreur assigné",
    delivered: "Livrée",
    picked_up: "Livreur en route",
    placed: "Commande confirmée",
    preparing: "En préparation",
    ready_for_pickup: "Prête au retrait",
    refunded: "Remboursée",
  };
  return (locale === "ar" ? ar : fr)[status] ?? status;
}
function modifierTotal(item: CatalogItem, selected: string[]) {
  return item.modifierGroups
    .flatMap((group) => group.options)
    .filter((option) => selected.includes(option.id))
    .reduce((sum, option) => sum + option.priceDeltaMillimes, 0);
}
function cartSubtotal(cart: Cart) {
  return cart.lines.reduce((sum, line) => sum + line.unitPriceMillimes * line.quantity, 0);
}
function readCart(): Cart | null {
  try {
    const value = localStorage.getItem("gobro-customer-cart");
    return value ? (JSON.parse(value) as Cart) : null;
  } catch {
    localStorage.removeItem("gobro-customer-cart");
    return null;
  }
}
function saveCart(cart: Cart | null) {
  if (cart) localStorage.setItem("gobro-customer-cart", JSON.stringify(cart));
  else localStorage.removeItem("gobro-customer-cart");
}
function readGuestLocation() {
  try {
    const raw = localStorage.getItem("gobro-guest-location");
    if (!raw) return DEFAULT_LOCATION;
    const value = JSON.parse(raw) as { latitude?: number; longitude?: number };
    return typeof value.latitude === "number" && typeof value.longitude === "number"
      ? { latitude: value.latitude, longitude: value.longitude }
      : DEFAULT_LOCATION;
  } catch {
    return DEFAULT_LOCATION;
  }
}
function errorText(error: unknown, locale: Locale) {
  if (error instanceof ApiError && error.status === 0) return tr(locale, "offline");
  return error instanceof Error
    ? error.message
    : locale === "ar"
      ? "حدث خطأ غير متوقع"
      : "Une erreur est survenue";
}
